import { useQuery } from '@tanstack/react-query'
import { apiFetch } from './api'

export interface AuthConfig {
  googleClientId: string
  /**
   * The Workspace domains whose accounts may sign in, most current first —
   * the firm runs one Workspace across `divorcewithaplan.com` and the older
   * `josephialaw.com`.
   */
  allowedDomains: string[]
}

/**
 * The permitted domains as a person reads them: `@a.com or @b.com`.
 *
 * One formatter, because the sign-in screen and the Users screen both name the
 * list and should name it identically. Mirrors `ALLOWED_GOOGLE_DOMAINS_LABEL`
 * on the API, which writes the same sentence into its error messages.
 */
export function domainLabel(domains: string[]): string {
  return domains.map((domain) => `@${domain}`).join(' or ')
}

/**
 * The public sign-in configuration, shared by every screen that names the
 * permitted domain.
 *
 * Fetched rather than compiled in, so the domains the interface advertises are
 * the same strings the API enforces — a hardcoded copy drifts, and the screen
 * telling an administrator which addresses are allowed is exactly where a drift
 * would mislead. One query key, so the sign-in screen and the Users screen share
 * a single request.
 */
export function useAuthConfig() {
  return useQuery({
    queryKey: ['auth-config'],
    queryFn: () => apiFetch<AuthConfig>('/auth/config'),
    staleTime: Infinity,
    retry: 1,
  })
}
